import { type PublicEsignDto } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Box, Button, Card, Center, Checkbox, Container, Group, Loader, Modal, Stack, Text, Textarea, TextInput, Title } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconClock, IconFileTypePdf, IconSignature } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { api, errorText, HttpError } from '../api';
import { formatDateTime } from '../format';
import { minHint } from '../minHint';

const dt = (iso: string) => formatDateTime(iso);

const STATUS_ALERT: Partial<Record<PublicEsignDto['status'], { color: string; title: string; text: string }>> = {
  anulowany: { color: 'gray', title: 'Podpisywanie anulowane', text: 'Prośba o podpis tego dokumentu została anulowana. W razie pytań skontaktuj się z nami.' },
  wygasly: { color: 'orange', title: 'Termin podpisu minął', text: 'Link do podpisu wygasł. Skontaktuj się z nami, aby otrzymać nowy.' },
  odrzucony: { color: 'red', title: 'Odmowa podpisu', text: 'Podpis tego dokumentu został odrzucony. Skontaktujemy się w sprawie dalszych kroków.' },
  finalizacja: { color: 'blue', title: 'Wszystkie strony podpisały', text: 'Trwa przygotowanie podpisanego dokumentu — za chwilę będzie gotowy do pobrania i trafi na Twój e-mail.' },
};

function SignForm({ token, d, onDone }: { token: string; d: PublicEsignDto; onDone: () => void }) {
  const [name, setName] = useState('');
  const [consents, setConsents] = useState<string[]>([]);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  // protokół odbioru: uwagi klienta do wykonanych prac (zapisane razem z podpisem)
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const required = d.consents.filter((c) => c.required).map((c) => c.code);
  const ready = name.trim().length >= 3 && required.every((c) => consents.includes(c));

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ destinationMasked: string }>(`/public/esign/${token}/code`, { method: 'POST' });
      setSentTo(r.destinationMasked);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const sign = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/public/esign/${token}/sign`, { method: 'POST', body: { typedName: name, consents, code, ...(d.remarks?.allowed ? { remarks } : {}) } });
      onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder radius="md" padding="lg">
      <Title order={4} mb="sm">
        Podpis dokumentu
      </Title>
      <Stack>
        <TextInput label="Imię i nazwisko" description={`Podpisujący: ${d.signer.name}`} value={name} onChange={(e) => setName(e.currentTarget.value)} disabled={!!sentTo} />
        {d.consents.map((c) => (
          <Checkbox
            key={c.code}
            disabled={!!sentTo}
            checked={consents.includes(c.code)}
            onChange={(e) => {
            const on = e.currentTarget.checked;
            setConsents((x) => (on ? [...x, c.code] : x.filter((y) => y !== c.code)));
          }}
            label={
              <>
                {c.text}
                {!c.required && (
                  <Text span size="xs" c="dimmed">
                    {' '}
                    (opcjonalnie)
                  </Text>
                )}
              </>
            }
          />
        ))}
        {d.remarks?.allowed && (
          <Textarea
            label="Uwagi do wykonanych prac (opcjonalnie)"
            description="Jeśli coś wymaga poprawki, opisz to tutaj — skontaktujemy się w sprawie poprawki. Bez uwag zostaw puste."
            autosize
            minRows={2}
            maxLength={2000}
            value={remarks}
            disabled={!!sentTo}
            onChange={(e) => setRemarks(e.currentTarget.value)}
          />
        )}
        {sentTo && (
          <>
            <Text size="sm">
              Wysłaliśmy 6-cyfrowy kod {d.signer.channel === 'sms' ? 'SMS-em na numer' : 'na adres'} <b>{sentTo}</b>. Kod jest ważny 10 minut.
            </Text>
            <TextInput
              label="Kod potwierdzenia"
              value={code}
              onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              size="md"
              w={200}
              data-autofocus
            />
          </>
        )}
        {error && (
          <Alert color="red" icon={<IconAlertTriangle />}>
            {error}
          </Alert>
        )}
        <Group justify="space-between">
          {sentTo ? (
            <Button variant="subtle" size="xs" onClick={() => void sendCode()} disabled={busy}>
              Wyślij kod ponownie
            </Button>
          ) : (
            <span />
          )}
          {sentTo ? (
            <Button size="md" leftSection={<IconSignature size={18} />} onClick={() => void sign()} loading={busy} disabled={code.length !== 6} color={d.company.color}>
              {d.documentKind === 'protokol' ? 'Podpisuję protokół' : 'Podpisuję dokument'}
            </Button>
          ) : (
            <Button size="md" onClick={() => void sendCode()} loading={busy} disabled={!ready} color={d.company.color}>
              {d.signer.channel === 'sms' ? 'Wyślij kod SMS' : 'Wyślij kod e-mailem'}
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

function DeclineDialog({ token, onClose, onDone }: { token: string; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal opened onClose={onClose} title="Odmowa podpisu">
      <Stack>
        <Text size="sm">Napisz, dlaczego nie podpiszesz dokumentu — przekażemy to opiekunowi sprawy.</Text>
        <Textarea autosize minRows={3} required description={minHint(reason)} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            color="red"
            loading={busy}
            disabled={reason.trim().length < 3}
            onClick={async () => {
              setBusy(true);
              try {
                await api(`/public/esign/${token}/decline`, { method: 'POST', body: { reason } });
                onDone();
              } catch (e) {
                setError(errorText(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Odmawiam podpisu
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Strona podpisującego (link z e-maila) — bez logowania, tylko ten dokument. */
export function PublicEsignPage() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['public-esign', token], queryFn: () => api<PublicEsignDto>(`/public/esign/${token}`), retry: false, refetchOnWindowFocus: false });
  const [declining, setDeclining] = useState(false);
  const refresh = () => qc.fetchQuery({ queryKey: ['public-esign', token], queryFn: () => api<PublicEsignDto>(`/public/esign/${token}?podglad`) });

  if (q.isLoading)
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  if (q.error)
    return (
      <Container size="sm" py={80}>
        <Alert color={q.error instanceof HttpError && q.error.status === 410 ? 'orange' : 'red'} title="Dokument niedostępny" icon={<IconAlertTriangle />}>
          {errorText(q.error)}
        </Alert>
      </Container>
    );
  const d = q.data!;
  const pdfUrl = `/api/public/esign/${token}/pdf`;
  const info = STATUS_ALERT[d.status];
  const docLabel = d.documentKind === 'aneks' ? 'Aneks' : d.documentKind === 'protokol' ? 'Protokół' : 'Umowa';
  const protocol = d.documentKind === 'protokol';

  return (
    <Box bg="var(--app-subtle)" mih="100vh" pb="xl">
      {d.demo && (
        <Box bg="var(--app-tint-orange)" py={6}>
          <Text ta="center" size="sm" c="var(--app-text-orange-strong)">
            Środowisko demonstracyjne — dane fikcyjne, SMS-y symulowane, pieczęć testowa. Podpis nie ma mocy prawnej.
          </Text>
        </Box>
      )}
      <Container size="md" pt="xl">
        <Box h={6} bg={d.company.color} style={{ borderRadius: 3 }} mb="md" />
        <Text fw={700} size="lg">
          {d.company.fullName}
        </Text>
        <Text size="sm" c="dimmed">
          {d.company.address}
          {d.company.nip ? ` · NIP ${d.company.nip}` : ''}
          {[d.company.phone, d.company.email].filter(Boolean).length ? ` · ${[d.company.phone, d.company.email].filter(Boolean).join(' · ')}` : ''}
        </Text>
        <Title order={2} mt="lg">
          {docLabel} {d.number}
        </Title>
        <Text c="dimmed">{d.title}</Text>

        <Group gap="xs" mt="md">
          {d.parties.map((p) => (
            <Badge
              key={p.role}
              size="lg"
              variant="light"
              color={p.status === 'podpisal' ? 'green' : p.status === 'odmowil' ? 'red' : 'gray'}
              leftSection={p.status === 'podpisal' ? <IconCheck size={14} /> : <IconClock size={14} />}
            >
              {p.role === 'klient' ? (protocol ? 'Klient' : 'Zamawiający') : 'Wykonawca'}: {p.name}
              {p.signedAt ? ` · ${dt(p.signedAt)}` : ''}
            </Badge>
          ))}
        </Group>

        {d.status === 'zakonczony' && (
          <Alert color="green" icon={<IconCheck />} mt="lg" title={protocol ? 'Protokół podpisany' : 'Dokument podpisany przez wszystkie strony'}>
            Podpisany dokument z kartą podpisów wysłaliśmy także na Twój e-mail.
            {protocol && d.remarks?.text && <Text size="sm" mt={4}>Zgłoszone uwagi: „{d.remarks.text}” — skontaktujemy się w sprawie poprawki.</Text>}
            <Button mt="sm" color="green" leftSection={<IconFileTypePdf size={16} />} component="a" href={pdfUrl} target="_blank" display="block" w="fit-content">
              Pobierz podpisany dokument
            </Button>
          </Alert>
        )}
        {info && (
          <Alert color={info.color} mt="lg" title={info.title}>
            {info.text}
          </Alert>
        )}
        {d.status === 'w_toku' && d.signer.status === 'podpisal' && (
          <Alert color="green" icon={<IconCheck />} mt="lg" title={`Podpisano ${d.signer.signedAt ? dt(d.signer.signedAt) : ''}`}>
            Dziękujemy. Gdy dokument podpisze druga strona, otrzymasz go e-mailem.
          </Alert>
        )}
        {d.status === 'w_toku' && d.signer.status === 'oczekuje' && !d.signer.isNext && (
          <Alert color="blue" mt="lg">
            Dokument czeka jeszcze na podpis drugiej strony. Wyślemy Ci wiadomość, gdy będzie można go podpisać.
          </Alert>
        )}

        {d.status !== 'zakonczony' && d.status !== 'anulowany' && (
          <Card withBorder radius="md" mt="lg" padding={0}>
            <Group justify="space-between" p="sm">
              <Text fw={600}>Treść dokumentu</Text>
              <Button variant="default" size="xs" leftSection={<IconFileTypePdf size={16} />} component="a" href={pdfUrl} target="_blank">
                Otwórz PDF
              </Button>
            </Group>
            <Box visibleFrom="sm">
              <iframe title="Dokument do podpisu" src={pdfUrl} style={{ width: '100%', height: '70vh', border: 0, borderTop: '1px solid var(--app-line-strong)' }} />
            </Box>
            <Text hiddenFrom="sm" size="sm" c="dimmed" px="sm" pb="sm">
              Otwórz PDF, aby przeczytać dokument przed podpisem.
            </Text>
          </Card>
        )}

        {d.signer.isNext && (
          <Box mt="lg">
            <SignForm token={token} d={d} onDone={() => void refresh()} />
            {!protocol && (
            <Text ta="right" mt="xs">
              <Anchor component="button" type="button" size="sm" c="dimmed" onClick={() => setDeclining(true)}>
                Nie podpiszę tego dokumentu
              </Anchor>
            </Text>
            )}
          </Box>
        )}

        <Text size="xs" c="dimmed" mt={48}>
          Podpis elektroniczny zwykły (art. 3 pkt 10 rozporządzenia eIDAS). Podpis potwierdzamy jednorazowym kodem; zapisujemy datę i godzinę, adres IP oraz przeglądarkę.
          Po podpisach wszystkich stron dokument otrzymuje kartę podpisów i pieczęć elektroniczną firmy — każda późniejsza zmiana pliku jest wykrywalna.
          <br />
          Link ważny do {dt(d.expiresAt)}. Skrót treści SHA-256 (jak w stopce dokumentu):{' '}
          <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{d.contentHash.slice(0, 16)}…{d.contentHash.slice(-8)}</span>
          <br />
          Skrót pliku PDF (SHA-256): <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{d.documentSha256}</span>
        </Text>
      </Container>
      {declining && (
        <DeclineDialog
          token={token}
          onClose={() => setDeclining(false)}
          onDone={() => {
            setDeclining(false);
            void refresh();
          }}
        />
      )}
    </Box>
  );
}
